import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTutorial } from './TutorialContext';
import type { TourStep } from './tourSteps';

const CARD_WIDTH = 360;
const GAP = 14; // space between target and tooltip
const PAD = 8; // spotlight padding around the target

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * Poll for the step's target element until it appears (pages such as the
 * analytics dashboards load asynchronously) or a timeout elapses. Returns the
 * element's viewport rect, and re-measures on scroll/resize.
 */
function useTargetRect(step: TourStep, active: boolean, pathname: string): {
  rect: Rect | null;
  resolved: boolean;
} {
  const [rect, setRect] = useState<Rect | null>(null);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    if (!active) return;
    if (!step.target) {
      setRect(null);
      setResolved(true);
      return;
    }

    let cancelled = false;
    let raf = 0;
    const started = Date.now();

    const measure = (el: Element): Rect => {
      const r = el.getBoundingClientRect();
      return { top: r.top, left: r.left, width: r.width, height: r.height };
    };

    const tick = () => {
      if (cancelled) return;
      const el = document.querySelector(step.target as string);
      if (el) {
        // Bring it into view, then measure on the next frame.
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        raf = requestAnimationFrame(() => {
          if (cancelled) return;
          setRect(measure(el));
          setResolved(true);
        });
        return;
      }
      if (Date.now() - started > 2500) {
        // Give up: element never appeared (e.g., empty feed) → render fallback.
        setRect(null);
        setResolved(true);
        return;
      }
      raf = requestAnimationFrame(tick);
    };

    setResolved(false);
    raf = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
    // Re-run when the step, route, or activation changes.
  }, [step.target, active, pathname]);

  // Keep the rect fresh while the step is shown.
  useEffect(() => {
    if (!active || !step.target || !resolved) return;
    const update = () => {
      const el = document.querySelector(step.target as string);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [active, step.target, resolved]);

  return { rect, resolved };
}

/** A static, non-interactive sample hazard card for the empty-feed case. */
const MockHazardCard: React.FC = () => (
  <div
    aria-hidden
    className="w-[320px] max-w-[86vw] bg-white dark:bg-deck-panel rounded-lg shadow-xl dark:ring-1 dark:ring-white/10 p-4 text-left pointer-events-none select-none"
  >
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-full bg-[#FF3EB5] text-white flex items-center justify-center font-semibold">
        A
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold text-gray-900 dark:text-white">Alex Morgan</div>
        <div className="text-xs text-gray-500 dark:text-deck-mute">Calgary, AB · 2h ago</div>
      </div>
      <span className="ml-auto text-xs font-semibold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: '#F97316' }}>
        High
      </span>
    </div>
    <div className="mt-3 h-24 rounded-md bg-gray-100 dark:bg-white/5 flex items-center justify-center text-gray-400 text-xs">
      Hazard photo
    </div>
    <p className="mt-3 text-sm text-gray-700 dark:text-gray-200">
      Icy walkway by the loading dock, reported before anyone slipped.
    </p>
    <div className="mt-3 flex flex-wrap gap-2">
      {['Rate risk', 'Comments', 'Tags', 'Resolve'].map((label) => (
        <span
          key={label}
          className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-gray-200"
        >
          {label}
        </span>
      ))}
    </div>
  </div>
);

const TutorialOverlay: React.FC = () => {
  const { active, stepIndex, steps, next, back, stop } = useTutorial();
  const navigate = useNavigate();
  const location = useLocation();
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardPos, setCardPos] = useState<{ top: number; left: number } | null>(null);

  const step = active ? steps[stepIndex] : undefined;

  // Navigate to the step's page when needed.
  useEffect(() => {
    if (!active || !step) return;
    if (location.pathname !== step.page) {
      navigate(step.page);
    }
  }, [active, step, location.pathname, navigate]);

  const onPage = !!step && location.pathname === step.page;
  const { rect, resolved } = useTargetRect(
    step ?? ({ id: '', page: '', title: '', body: '' } as TourStep),
    active && onPage,
    location.pathname,
  );

  const showSpotlight = !!rect && step?.spotlight !== false;
  const showMockCard = !!step?.mockCardFallback && resolved && !rect;

  // Position the tooltip card relative to the target (or centered).
  useLayoutEffect(() => {
    if (!active || !step) return;
    const cardEl = cardRef.current;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const ch = cardEl?.offsetHeight ?? 200;
    const cw = Math.min(CARD_WIDTH, vw - 24);

    if (!rect || step.spotlight === false || step.placement === 'center') {
      // Centered card.
      setCardPos({ top: Math.max(16, (vh - ch) / 2), left: Math.max(12, (vw - cw) / 2) });
      return;
    }

    let left = rect.left + rect.width / 2 - cw / 2;
    left = Math.min(Math.max(12, left), vw - cw - 12);

    const wantsTop = step.placement === 'top';
    let top: number;
    if (wantsTop) {
      top = rect.top - GAP - ch;
      if (top < 12) top = rect.top + rect.height + GAP; // flip below if no room
    } else {
      top = rect.top + rect.height + GAP;
      if (top + ch > vh - 12) top = Math.max(12, rect.top - GAP - ch); // flip above
    }
    top = Math.min(Math.max(12, top), vh - ch - 12);
    setCardPos({ top, left });
  }, [active, step, rect, resolved, stepIndex]);

  // Escape ends the tour.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') stop(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, stop]);

  const handleNext = useCallback(() => {
    void next();
  }, [next]);

  if (!active || !step) return null;

  const isLast = stepIndex === steps.length - 1;
  const isFirst = stepIndex === 0;
  // Light scrim for analytics (so charts show through); darker otherwise.
  const scrimOpacity = step.spotlight === false && !step.placement ? 0.5 : step.analytics ? 0.28 : 0.55;

  const overlay = (
    <div className="fixed inset-0 z-[10000]" role="dialog" aria-modal="true" aria-label="Product tour">
      {/* Base scrim — captures clicks so the underlying page can't be interacted with. */}
      {!showSpotlight && (
        <div
          className="absolute inset-0"
          style={{ backgroundColor: `rgba(0,0,0,${scrimOpacity})`, transition: 'background-color 150ms' }}
        />
      )}

      {/* Spotlight cutout via a large box-shadow "hole" around the target. */}
      {showSpotlight && rect && (
        <>
          <div className="absolute inset-0" style={{ pointerEvents: 'auto' }} />
          <div
            className="absolute rounded-xl"
            style={{
              top: rect.top - PAD,
              left: rect.left - PAD,
              width: rect.width + PAD * 2,
              height: rect.height + PAD * 2,
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)',
              outline: '2px solid #FF3EB5',
              outlineOffset: '2px',
              pointerEvents: 'none',
              transition: 'all 180ms ease',
            }}
          />
        </>
      )}

      {/* Sample card shown when the real target is absent (empty feed). */}
      {showMockCard && (
        <div className="absolute left-1/2 -translate-x-1/2" style={{ top: '18%' }}>
          <MockHazardCard />
        </div>
      )}

      {/* Tooltip card */}
      <div
        ref={cardRef}
        className="absolute bg-white dark:bg-deck-panel text-left rounded-2xl shadow-2xl ring-1 ring-black/5 dark:ring-white/10 p-5"
        style={{
          width: Math.min(CARD_WIDTH, window.innerWidth - 24),
          top: cardPos?.top ?? '50%',
          left: cardPos?.left ?? '50%',
          pointerEvents: 'auto',
        }}
      >
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-gray-400 dark:text-deck-faint">
            Step {stepIndex + 1} of {steps.length}
          </span>
          <button
            onClick={() => stop(false)}
            className="text-xs font-medium text-gray-400 hover:text-gray-600 dark:hover:text-white"
            aria-label="Skip tour"
          >
            Skip
          </button>
        </div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">{step.title}</h3>
        <p className="mt-1.5 text-sm text-gray-600 dark:text-deck-mute leading-relaxed">{step.body}</p>

        <div className="mt-4 flex items-center justify-between">
          {/* Progress dots */}
          <div className="flex items-center gap-1.5">
            {steps.map((s, i) => (
              <span
                key={s.id}
                className={`h-1.5 rounded-full transition-all ${
                  i === stepIndex ? 'w-4 bg-[#FF3EB5]' : 'w-1.5 bg-gray-300 dark:bg-white/20'
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {!isFirst && (
              <button
                onClick={back}
                className="px-3 py-1.5 text-sm font-medium rounded-full text-gray-600 dark:text-deck-mute hover:bg-gray-100 dark:hover:bg-white/10"
              >
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              className="px-4 py-1.5 text-sm font-semibold rounded-full text-white bg-[#FF3EB5] hover:bg-[#E635A3] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FF3EB5]"
            >
              {isLast ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
};

export default TutorialOverlay;
